import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Input, Space, Tag, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { AxiosError } from "axios";
import Highlighter from "react-highlight-words";

import {
    convertDataBrParaUS,
    convertMoedaBRDoJSNovo,
    formatDateStringToDateAndTime
} from "../../utils/Conversores";
import { formatCpf, formatCpfCnpj, onlyNumbers } from "../../utils/Masks";
import { findChunksCpfCnpj, findChunksText, HIGHLIGHT_STYLE } from "../../utils/Highlight";
import { IPendingInvoice } from "../../interfaces";
import PendingInvoicesService from "../../services/http/services/PendingInvoicesService";
import useAuth from "../../hooks/useAuth";
import { useTableParams } from "../../hooks/useTableParams";
import { getSortOrder } from "../../hooks/SortOrder";
import { useGetColumnSearchPropsData } from "../../hooks/FilterTable/date";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import TableActionButton from "../../components/TableActionButton";
import { ThemeContext } from "../../contexts/ThemeContext";
import { EmitError } from "../../utils/EmitError";

export interface ITableData extends IPendingInvoice {
    rowKey: number;
    createdAt: Date;
}

export type TypeSortPending =
    | "createdAt"
    | "date_issue"
    | "dateIssue"
    | "value"
    | "company"
    | "name"
    | "user"
    | "cpf"
    | "type"
    | "status"
    | "number";

export interface ICityPendingInvoicesFilters {
    name: string | null;
    cpf: string | null;
    company: string | null;
    cnpj: string | null;
    type: string | null;
    status: string[] | string | null;
    createdAt: string | null;
    dateIssue: string | null;
}

export default function useCityInvoices() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const { activeCity } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [totalInvoices, setTotalInvoices] = useState<number>(0);
    const [showModalInvoice, setShowModalInvoice] = useState<boolean>(false);
    const [invoiceSelected, setInvoiceSelected] = useState<ITableData>();

    const refFuncaoLimparCreatedAt = useRef<any>(null);
    const refFuncaoLimparDateIssue = useRef<any>(null);
    const nameInputRef = useRef<any>(null);
    const companyInputRef = useRef<any>(null);

    const {
        tableParams,
        handleTableChange,
        handleCustomChange,
        handlePaginationChange,
        handleFilterClear,
        handleClearAllFilters,
        lastSorterRef,
    } = useTableParams<ICityPendingInvoicesFilters, TypeSortPending>({
        storageKey: "@NotaMunicipio:pendingInvoices",
        defaultFilters: {
            name: null,
            cpf: null,
            company: null,
            cnpj: null,
            type: null,
            status: null,
            createdAt: null,
            dateIssue: null,
        },
        defaultSort: {
            field: "createdAt",
            order: "desc",
        },
        defaultPageSize: 50,
    });

    const [searchName, setSearchName] = useState(tableParams.filters.name || "");
    const [searchCpf, setSearchCpf] = useState(tableParams.filters.cpf ? formatCpf(tableParams.filters.cpf) : "");
    const [searchCompany, setSearchCompany] = useState(tableParams.filters.company || "");
    const [searchCnpj, setSearchCnpj] = useState(tableParams.filters.cnpj ? formatCpfCnpj(tableParams.filters.cnpj) : "");

    useEffect(() => {
        setSearchName(tableParams.filters.name || "");
    }, [tableParams.filters.name]);

    useEffect(() => {
        setSearchCpf(tableParams.filters.cpf ? formatCpf(tableParams.filters.cpf) : "");
    }, [tableParams.filters.cpf]);

    useEffect(() => {
        setSearchCompany(tableParams.filters.company || "");
    }, [tableParams.filters.company]);

    useEffect(() => {
        setSearchCnpj(tableParams.filters.cnpj ? formatCpfCnpj(tableParams.filters.cnpj) : "");
    }, [tableParams.filters.cnpj]);

    const handleShowModalInvoice = useCallback(() => {
        setShowModalInvoice((prevState) => !prevState);
    }, []);

    const fetchInvoices = useCallback(async (signal?: AbortSignal) => {
        if (!activeCity?.id) return;
        try {
            setIsLoading(true);

            let sortField = tableParams.sorter?.field || "createdAt";
            if (sortField === "name") sortField = "user";

            const response = await PendingInvoicesService.listPendingInvoices({
                cityId: activeCity.id,
                page: tableParams.pagination.current,
                size: tableParams.pagination.pageSize,
                sort: sortField,
                order: tableParams.sorter?.order === "asc" ? "asc" : "desc",
                name: tableParams.filters.name || undefined,
                cpf: tableParams.filters.cpf ? onlyNumbers(tableParams.filters.cpf) : undefined,
                company: tableParams.filters.company || undefined,
                cnpj: tableParams.filters.cnpj ? onlyNumbers(tableParams.filters.cnpj) : undefined,
                type: tableParams.filters.type || undefined,
                status: Array.isArray(tableParams.filters.status)
                    ? tableParams.filters.status
                    : tableParams.filters.status
                        ? [tableParams.filters.status]
                        : undefined,
                createdAt: tableParams.filters.createdAt ? convertDataBrParaUS(tableParams.filters.createdAt) : undefined,
                dateIssue: tableParams.filters.dateIssue ? convertDataBrParaUS(tableParams.filters.dateIssue) : undefined,
                signal: signal || new AbortController().signal,
            });

            const invoicesList = response.pending_invoices || response.invoices || [];
            const formatted: ITableData[] = invoicesList.map((inv) => ({
                ...inv,
                rowKey: inv.id,
                createdAt: inv.created_at ? new Date(inv.created_at) : new Date(),
            }));

            setTableData(formatted);
            setTotalInvoices(response.total || 0);
            setIsLoading(false);
        } catch (err) {
            if (!(err instanceof AxiosError && err.name === "CanceledError")) {
                EmitError(err, true, "cityPendingInvoices");
                setIsLoading(false);
            }
        }
    }, [activeCity?.id, tableParams]);

    useEffect(() => {
        const controller = new AbortController();
        fetchInvoices(controller.signal);
        return () => controller.abort();
    }, [fetchInvoices]);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        tableParams.filters.createdAt
            ? {
                key: "createdAt",
                label: "Data de Cadastro",
                value: tableParams.filters.createdAt,
                onClear: () => handleFilterClear("createdAt"),
            }
            : null,
        tableParams.filters.dateIssue
            ? {
                key: "dateIssue",
                label: "Data de Emissão",
                value: tableParams.filters.dateIssue,
                onClear: () => handleFilterClear("dateIssue"),
            }
            : null,
        tableParams.filters.type
            ? {
                key: "type",
                label: "Tipo",
                value: tableParams.filters.type,
                onClear: () => handleFilterClear("type"),
            }
            : null,
        tableParams.filters.name
            ? {
                key: "name",
                label: "Cidadão",
                value: tableParams.filters.name,
                onClear: () => {
                    setSearchName("");
                    handleFilterClear("name");
                },
            }
            : null,
        tableParams.filters.cpf
            ? {
                key: "cpf",
                label: "CPF",
                value: formatCpf(tableParams.filters.cpf),
                onClear: () => {
                    setSearchCpf("");
                    handleFilterClear("cpf");
                },
            }
            : null,
        tableParams.filters.company
            ? {
                key: "company",
                label: "Empresa",
                value: tableParams.filters.company,
                onClear: () => {
                    setSearchCompany("");
                    handleFilterClear("company");
                },
            }
            : null,
        tableParams.filters.cnpj
            ? {
                key: "cnpj",
                label: "CNPJ",
                value: formatCpfCnpj(tableParams.filters.cnpj),
                onClear: () => {
                    setSearchCnpj("");
                    handleFilterClear("cnpj");
                },
            }
            : null,
        tableParams.filters.status
            ? {
                key: "status",
                label: "Status",
                value: Array.isArray(tableParams.filters.status)
                    ? tableParams.filters.status.join(", ")
                    : tableParams.filters.status,
                onClear: () => handleFilterClear("status"),
            }
            : null,
    ];

    const getTypeTag = (type?: string) => {
        let color = "blue";
        if (type === "NFC") color = "purple";
        if (type === "NFE") color = "cyan";

        return (
            <Tag
                color={color}
                style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
            >
                {type || "NFS"}
            </Tag>
        );
    };

    const getStatusTag = (status?: string) => {
        if (!status) return <Tag>—</Tag>;
        const s = status.toLowerCase();
        if (s.includes("conclu") || s.includes("sucess") || s.includes("finaliz")) {
            return <Tag color="success">{status}</Tag>;
        }
        if (s.includes("recus") || s.includes("erro") || s.includes("cancel")) {
            return <Tag color="error">{status}</Tag>;
        }
        if (s.includes("pend") || s.includes("aguard") || s.includes("process") || s.includes("import")) {
            return <Tag color="warning">{status}</Tag>;
        }
        return <Tag color="default">{status}</Tag>;
    };

    const columns: ColumnsType<ITableData> = [
        {
            title: "Data de Cadastro",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 180,
            sorter: true,
            sortOrder: getSortOrder("createdAt", tableParams.sorter),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({
                dataIndex: "createdAt",
                ref: refFuncaoLimparCreatedAt,
                onFilterClear: () => handleFilterClear("createdAt"),
                initialValue: tableParams.filters.createdAt,
            }),
            render: (data: any) => (
                <span style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 13, whiteSpace: "nowrap" }}>
                    {data ? formatDateStringToDateAndTime(data, true) : "—"}
                </span>
            ),
        },
        {
            title: "Data Emissão",
            dataIndex: "date_issue",
            key: "date_issue",
            width: 170,
            sorter: true,
            sortOrder: getSortOrder("date_issue", tableParams.sorter),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({
                dataIndex: "dateIssue",
                ref: refFuncaoLimparDateIssue,
                onFilterClear: () => handleFilterClear("dateIssue"),
                initialValue: tableParams.filters.dateIssue,
            }),
            render: (date: string) => (
                <span style={{ color: isDark ? "#d1d5db" : "#334155", fontSize: 13, whiteSpace: "nowrap" }}>
                    {date ? formatDateStringToDateAndTime(date, true) : "—"}
                </span>
            ),
        },
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type",
            width: 90,
            align: "center",
            sorter: true,
            sortOrder: getSortOrder("type", tableParams.sorter),
            showSorterTooltip: false,
            filters: [
                { text: "NFS", value: "NFS" },
                { text: "NFC", value: "NFC" },
                { text: "NFE", value: "NFE" },
            ],
            filteredValue: tableParams.filters.type ? [tableParams.filters.type] : null,
            filterMultiple: false,
            render: (type: string) => getTypeTag(type),
        },
        {
            title: "Cidadão",
            dataIndex: "name",
            key: "name",
            width: 240,
            sorter: true,
            sortOrder: getSortOrder("name", tableParams.sorter),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 270, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#ccc" : "#475569", marginBottom: 2 }}>
                        Buscar por Cidadão / CPF
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: isDark ? "#aaa" : "#64748b", marginBottom: 4 }}>
                            Nome
                        </div>
                        <Input
                            ref={nameInputRef}
                            placeholder="Buscar por nome..."
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: {
                                        name: searchName.trim() || null,
                                        cpf: searchCpf.trim() ? onlyNumbers(searchCpf) : null,
                                    },
                                });
                            }}
                            allowClear
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: isDark ? "#aaa" : "#64748b", marginBottom: 4 }}>
                            CPF
                        </div>
                        <Input
                            placeholder="000.000.000-00"
                            value={searchCpf}
                            onChange={(e) => setSearchCpf(formatCpf(e.target.value))}
                            onPressEnter={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: {
                                        name: searchName.trim() || null,
                                        cpf: searchCpf.trim() ? onlyNumbers(searchCpf) : null,
                                    },
                                });
                            }}
                            allowClear
                        />
                    </div>
                    <Space style={{ justifyContent: "space-between", marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: {
                                        name: searchName.trim() || null,
                                        cpf: searchCpf.trim() ? onlyNumbers(searchCpf) : null,
                                    },
                                });
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchName("");
                                setSearchCpf("");
                                if (clearFilters) clearFilters();
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { name: null, cpf: null },
                                });
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: (tableParams.filters.name || tableParams.filters.cpf) ? "#1677ff" : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => nameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b", lineHeight: "1.3" }}>
                        {tableParams.filters.name ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[tableParams.filters.name]}
                                autoEscape={true}
                                textToHighlight={record.name || "—"}
                                findChunks={findChunksText}
                            />
                        ) : (
                            record.name || "—"
                        )}
                    </span>
                    {record.cpf && (
                        <span style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 12, fontWeight: 500, letterSpacing: "0.2px", whiteSpace: "nowrap", lineHeight: "1.2" }}>
                            {tableParams.filters.cpf ? (
                                <Highlighter
                                    highlightStyle={HIGHLIGHT_STYLE}
                                    searchWords={[tableParams.filters.cpf]}
                                    autoEscape={true}
                                    textToHighlight={formatCpf(record.cpf)}
                                    findChunks={findChunksCpfCnpj}
                                />
                            ) : (
                                formatCpf(record.cpf)
                            )}
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: "Empresa / Estabelecimento",
            dataIndex: "company",
            key: "company",
            width: 250,
            sorter: true,
            sortOrder: getSortOrder("company", tableParams.sorter),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 270, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#ccc" : "#475569", marginBottom: 2 }}>
                        Buscar por Empresa / CNPJ
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: isDark ? "#aaa" : "#64748b", marginBottom: 4 }}>
                            Razão Social / Empresa
                        </div>
                        <Input
                            ref={companyInputRef}
                            placeholder="Buscar por razão social..."
                            value={searchCompany}
                            onChange={(e) => setSearchCompany(e.target.value)}
                            onPressEnter={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: {
                                        company: searchCompany.trim() || null,
                                        cnpj: searchCnpj.trim() ? onlyNumbers(searchCnpj) : null,
                                    },
                                });
                            }}
                            allowClear
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: isDark ? "#aaa" : "#64748b", marginBottom: 4 }}>
                            CNPJ
                        </div>
                        <Input
                            placeholder="00.000.000/0000-00"
                            value={searchCnpj}
                            onChange={(e) => setSearchCnpj(formatCpfCnpj(e.target.value))}
                            onPressEnter={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: {
                                        company: searchCompany.trim() || null,
                                        cnpj: searchCnpj.trim() ? onlyNumbers(searchCnpj) : null,
                                    },
                                });
                            }}
                            allowClear
                        />
                    </div>
                    <Space style={{ justifyContent: "space-between", marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: {
                                        company: searchCompany.trim() || null,
                                        cnpj: searchCnpj.trim() ? onlyNumbers(searchCnpj) : null,
                                    },
                                });
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchCompany("");
                                setSearchCnpj("");
                                if (clearFilters) clearFilters();
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { company: null, cnpj: null },
                                });
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: (tableParams.filters.company || tableParams.filters.cnpj) ? "#1677ff" : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => companyInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#f0f0f0" : "#0f172a", lineHeight: "1.3" }}>
                        {tableParams.filters.company ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[tableParams.filters.company]}
                                autoEscape={true}
                                textToHighlight={record.company || "—"}
                                findChunks={findChunksText}
                            />
                        ) : (
                            record.company || "—"
                        )}
                    </span>
                    {record.cpf_cnpj && (
                        <span style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 12, fontWeight: 500, letterSpacing: "0.2px", whiteSpace: "nowrap", lineHeight: "1.2" }}>
                            {tableParams.filters.cnpj ? (
                                <Highlighter
                                    highlightStyle={HIGHLIGHT_STYLE}
                                    searchWords={[tableParams.filters.cnpj]}
                                    autoEscape={true}
                                    textToHighlight={formatCpfCnpj(record.cpf_cnpj)}
                                    findChunks={findChunksCpfCnpj}
                                />
                            ) : (
                                formatCpfCnpj(record.cpf_cnpj)
                            )}
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: "Valor",
            dataIndex: "value",
            key: "value",
            align: "right",
            width: 130,
            sorter: true,
            sortOrder: getSortOrder("value", tableParams.sorter),
            showSorterTooltip: false,
            render: (value: number) => (
                <Tooltip color="#389e0d" title="Valor da nota pendente">
                    <strong style={{ color: isDark ? "#52c41a" : "#237804", fontSize: 13.5, whiteSpace: "nowrap" }}>
                        R$ {convertMoedaBRDoJSNovo(value || 0)}
                    </strong>
                </Tooltip>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            align: "center",
            width: 140,
            sorter: true,
            sortOrder: getSortOrder("status", tableParams.sorter),
            showSorterTooltip: false,
            filters: [
                { text: "Pendente", value: "Pendente" },
                { text: "Importado", value: "Importado" },
                { text: "Concluído", value: "Concluído" },
                { text: "Em processamento", value: "Em processamento" },
                { text: "Alerta", value: "Alerta" },
                { text: "Recusado", value: "Recusado" },
            ],
            filteredValue: tableParams.filters?.status
                ? Array.isArray(tableParams.filters.status)
                    ? tableParams.filters.status
                    : [tableParams.filters.status]
                : null,
            render: (status: string) => getStatusTag(status),
        },
        {
            title: "Ações",
            key: "actions",
            width: 80,
            align: "center",
            fixed: "right",
            render: (_: any, record: ITableData) => (
                <TableActionButton
                    action="view"
                    tooltip="Visualizar detalhes da nota"
                    onClick={(e) => {
                        e.stopPropagation();
                        setInvoiceSelected(record);
                        handleShowModalInvoice();
                    }}
                />
            ),
        },
    ];

    return {
        columns,
        isLoading,
        tableData,
        totalInvoices,
        showModalInvoice,
        handleShowModalInvoice,
        invoiceSelected,
        setInvoiceSelected,
        tableParams,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        activeFilterItems,
        lastSorterRef,
        reloadInvoices: fetchInvoices,
    };
}
